import React, { memo, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { currentStepSelector, formSummarySelector } from '../../../store/modules/steps/selectors';
import { FormStep, FormStepProps, StepsState } from '../../../model/global';
import { CustomFormFieldState } from '../../FormControls/CustomFormField/CustomFormField';
import { getFileslotId } from '../../utils';
import { stepsActions } from '../../../store/modules/steps/reducer';
import FileDetails from './FileDetails';
import AlertBox from './AlertBox';
import IconRenderer from 'global/react-mui/atoms/IconRenderer';
import Button from 'global/react-mui/atoms/Button';

const FileAttachments: React.FC<FormStepProps> = ({ onStepControlsChange, data: { stepTemplate } }) => {
	const dispatch = useDispatch();

	const step = useSelector(currentStepSelector) as FormStep<'main'>
	const translation = step?.stepData?.fieldTranslations
	const uploadSettings = step?.stepData?.fileUploadData

	// check data spec for if at least one file is required for the step to be considered valid or not
	let minFilesRequired = 0;
	if (uploadSettings?.requiresAtLeastOneFile === true) minFilesRequired = 1

	const summary = useSelector(formSummarySelector) as StepsState['formSummary'];
	const currentFiles = Object.values(summary.steps[stepTemplate]);

	const fileAddError = currentFiles && currentFiles.length
		? (currentFiles[currentFiles.length - 1]?.invalidReason || '')
		: ''
	const [fileSlots, setFileSlots] = useState<string[]>([...new Array(currentFiles.length)].map(c => { return 'coffee' }));

	const toggleFileSlot = (mode: 'add' | 'remove', event?: React.MouseEvent<HTMLButtonElement, MouseEvent>): void => {
		event && event.preventDefault();
		mode === 'add'
			? setFileSlots([...fileSlots, 'coffee'])
			: setFileSlots([...fileSlots.splice(-1)])
	}

	const getFileDetailsAtSlotIndex = (index: number): CustomFormFieldState['fileDetails'] | undefined => {
		if (index >= 0 && currentFiles.length > index) {
			return currentFiles[index]?.fileDetails
		}
	};

	// initialise the step data once
	useEffect(() => {

		// don't re-init fields
		if (summary.initialized[stepTemplate]) return

		const initialData: Record<string, CustomFormFieldState> = {}
		dispatch(stepsActions.initializeStepPayloadValues({
			stepTemplate,
			stepPayload: initialData,
			initialize: true
		}))

		dispatch(stepsActions.updateStepPayloadValues({
			stepTemplate,
			stepPayload: []
		}))

		// set validitiy manually
		let validity = true;
		if (currentFiles.length === 0 && minFilesRequired > 0) validity = false
		dispatch(stepsActions.declareStepValidity({
			stepTemplate,
			validity
		}))

	}, [currentFiles.length, dispatch, minFilesRequired, stepTemplate, summary.initialized])

	const removeFileUpload = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>, fileIndex: number): void => {
		event.preventDefault()
		const id = getFileslotId(fileIndex)
		const fileInput = document.getElementById(id) as HTMLInputElement
		if (fileInput) {
			// reset the actual file input to empty
			fileInput.value = ''
			// update redux
			const newFilesList = [...Object.values(summary.steps[stepTemplate])]
			newFilesList.splice(newFilesList.length - 1, 1)

			dispatch(stepsActions.updateStepPayloadValues({
				stepTemplate,
				stepPayload: newFilesList as any,
				refreshSummary: true
			}));

			// set validitiy manually
			let validity = true
			if ((newFilesList as Array<any>).length === 0 && minFilesRequired > 0) validity = false
			dispatch(stepsActions.declareStepValidity({
				stepTemplate,
				validity
			}));

			// finally remove a file slot
			toggleFileSlot('remove')
		}
	}

	const toggleFixedInput = (index: number) => {
		const id = getFileslotId(index);
		document.getElementById(id)?.click()
	}

	const shouldShowAddFileButton = (): boolean => {
		return (uploadSettings && Number.isInteger(uploadSettings.maxFileSlots))
			? (uploadSettings.maxFileSlots > currentFiles.length)
			: false
	}

	const getAddButtonDisabledState = (): boolean => {
		// check current file error
		if (fileAddError) return true

		// check if latest file has been added yet
		if (fileSlots.length > currentFiles.length) return true;

		// haven't added a file yet so enable it
		if (currentFiles.length === 0) return false

		// const fileChosen = getFileDetailsAtSlotIndex(currentFiles.length - 1);
		// if (!fileChosen) return true;
		return false
	}

	return (
		<div className={'file-attachment'}>
			{fileSlots?.map((file, fileIndex) => {
				const fileChosen = getFileDetailsAtSlotIndex(fileIndex)
				return (
					<div key={`attachment-${fileIndex}`} className={'file-wrapper'}>
						<div className={'file-box'}>
							<Button
								onClick={e => toggleFixedInput(fileIndex)}
								variant="outlined"
								disabled={fileSlots.length - 1 !== fileIndex}
								className={'file-choose'}>
								{fileChosen ? translation?.changeFile : translation?.chooseAFile}
							</Button>
							{fileChosen && fileSlots?.length > 0 && fileSlots?.length - 1 === fileIndex &&
								(<a onClick={e => removeFileUpload(e, fileIndex)}>
									<IconRenderer
										shape="squared"
										// bgColor="#E0E0E0"
										color="#A7A7A7"
										boxProps={{ justifyContent: 'center' }}
										icon={'close'}
									/>
								</a>)
							}
						</div>
						{fileChosen && <FileDetails
							fileNameLabel={translation?.fileName}
							fileTypeLabel={translation?.fileType}
							fileSizeLabel={translation?.fileSize}
							fileDetails={fileChosen}
						/>}
					</div>
				)
			})}

			{fileAddError && <AlertBox alert={fileAddError} />}

			{shouldShowAddFileButton() && <button
				onClick={e => toggleFileSlot('add', e)}
				className={'add-btn'}
				disabled={getAddButtonDisabledState()}
			>
				{translation?.addAnotherFile}
			</button>}
		</div>
	)
}

export default memo(FileAttachments);

